import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import useOrders from "../../Services/OrderServices/useOrders";

const OtherPaymentMethod = ({ userID, active = false }) => {
  const {
    getPosPaymentMethods,
    addPosPaymentMethod,
    deletePosPaymentMethod,
  } = useOrders();

  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const loadPaymentMethods = async () => {
    if (!userID) {
      setPaymentMethodsList([]);
      return;
    }

    setLoading(true);
    try {
      const res = await getPosPaymentMethods(userID);
      if (res.success) {
        setPaymentMethodsList(res.data || []);
      } else {
        setPaymentMethodsList([]);
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    if (!userID) return toast.error("Please select a client first.");
    if (!paymentMethod.trim()) {
      return toast.error("Please enter a payment method.");
    }

    setAddLoading(true);
    try {
      const res = await addPosPaymentMethod({
        userid: userID,
        paymentMethod: paymentMethod.trim(),
      });

      if (res.success) {
        toast.success(res.message);
        setPaymentMethod("");
        await loadPaymentMethods();
      } else {
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to add payment method");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (!userID) return toast.error("Please select a client first.");

    setLoading(true);
    try {
      const res = await deletePosPaymentMethod({
        userid: userID,
        paymentMethodId,
      });

      if (res.success) {
        toast.success(res.message);
        await loadPaymentMethods();
      } else {
        toast.error(res.message);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete payment method");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    loadPaymentMethods();
  }, [userID, active]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-gray-50 p-4">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-[#01C37D]">
          <CreditCard size={16} />
          Add Payment Method
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <div className="mb-1 text-xs text-gray-500">Payment Method</div>
            <input
              type="text"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
              placeholder="e.g. Paytm, PhonePe, Card Swipe"
              disabled={addLoading}
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleAddPaymentMethod}
              disabled={addLoading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
            >
              <Plus size={16} />
              {addLoading ? "Adding…" : "Add Method"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-white">
        <div className="border-b px-4 py-3 text-sm font-semibold">
          Existing Payment Methods
        </div>

        <div className="p-4">
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading payment methods…</div>
          ) : paymentMethodsList.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {paymentMethodsList.map((method, index) => (
                <div key={method.id || index} className="rounded-2xl border p-4">
                  <div className="mb-3 flex items-start justify-between gap-3 border-b pb-2">
                    <div className="text-sm font-semibold text-[#01C37D]">
                      {method.method}
                    </div>

                    <button
                      onClick={() => handleDeletePaymentMethod(method.id)}
                      disabled={loading}
                      className="inline-flex items-center gap-1 rounded-lg bg-rose-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      <Trash2 size={13} />
                      Remove
                    </button>
                  </div>

                  <div className="flex justify-between gap-3 text-sm">
                    <span className="text-gray-500">Payment Method</span>
                    <span className="font-medium text-right">{method.method}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed p-6 text-sm text-gray-500">
              No payment methods found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtherPaymentMethod;